import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'

// setTimeout delays are stored as a 32-bit signed int; a larger value overflows
// and the callback fires almost immediately. Don't arm the timer past this.
const MAX_TIMEOUT = 2_147_483_647

/** Milliseconds until `ms` (negative or 0 once it's in the past). */
const msUntil = (ms: number) => DateTime.fromMillis(ms).diffNow().toMillis()

/**
 * Returns whether `date` is in the future, flipping to `false` on its own once
 * that moment passes — so a future-scheduled clock-in transitions live without
 * waiting for the next time-clock poll. Returns `false` for null/undefined.
 */
export const useIsFutureTime = (date?: Date | null): boolean => {
  const ms = date?.getTime() ?? null
  const [isFuture, setIsFuture] = useState(() => ms !== null && msUntil(ms) > 0)

  useEffect(() => {
    if (ms === null) {
      setIsFuture(false)
      return
    }
    const remaining = msUntil(ms)
    setIsFuture(remaining > 0)

    // Only arm the auto-flip timer when the delay fits in setTimeout's range;
    // for a far-future time leave it `true` and let the next time-clock poll /
    // re-render re-evaluate (rather than overflowing and firing immediately).
    if (remaining > 0 && remaining <= MAX_TIMEOUT) {
      const timer = setTimeout(() => setIsFuture(false), remaining)
      return () => clearTimeout(timer)
    }
  }, [ms])

  return isFuture
}
